import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Header } from "../components/Header";
import { Breadcrumbs, PublicFooter } from "../components/PublicChrome";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { SAMPLES } from "../lib/samples";
import { SIZES, TEMPLATES, eur, shieldMm } from "../lib/shield";
import { SizeChart } from "../components/SizeChart";
import { WallPreview } from "../components/WallPreview";
import { useSeo, breadcrumbs, ORGANIZATION, SITE_URL } from "../lib/seo";
import { ReviewStrip, useReviews, withRatingLd } from "../components/ReviewStrip";

const CRUMBS = [["Home", "/"], ["Shields & prices", "/shields"]];
const IMG = `${SITE_URL}/img/personalised-sports-shield-bedroom-wall.jpg`;
const DESC = "Personalised football cards & player shields with photo, name, crest and six stats. Four sizes from €16, printed on rigid board in Ireland.";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    ORGANIZATION,
    {
      "@type": "Product", name: "Personalised Sports Player Shield", description: DESC, image: [IMG, `${SITE_URL}/img/og-home.jpg`],
      brand: { "@type": "Brand", name: "sportsshields.ie" }, category: "Personalised wall art", sku: "sports-shield",
      offers: SIZES.map((s) => ({
        "@type": "Offer", sku: `shield_${s.id}`, name: `${s.name} (shield ${shieldMm(s).w} × ${s.h} mm)`, price: (s.price / 100).toFixed(2), priceCurrency: "EUR",
        availability: "https://schema.org/InStock", itemCondition: "https://schema.org/NewCondition", url: `${SITE_URL}/shields#${s.id}`, seller: { "@id": `${SITE_URL}/#org` },
      })),
    },
    breadcrumbs(CRUMBS),
  ],
};

const withRating = (rv) => {
  const [org, product, ...rest] = JSON_LD["@graph"];
  return { ...JSON_LD, "@graph": [org, withRatingLd(product, rv), ...rest] };
};

export default function Shields() {
  const nav = useNavigate();
  const rv = useReviews();
  useSeo({ title: "Player Shield Sizes & Prices – From €16 | sportsshields.ie", description: DESC, path: "/shields", image: IMG, jsonLd: withRating(rv) });
  useEffect(() => { const h = window.location.hash.slice(1); if (h) document.getElementById(h)?.scrollIntoView(); }, []);
  return (
    <div className="min-h-screen">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-12">
        <Breadcrumbs items={CRUMBS} />
        <section className="mt-4 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl" data-testid="shields-title">Personalised football cards &amp; player shields</h1>
            <p className="mt-4 max-w-lg text-slate-600">{DESC}</p>
            <ul className="mt-5 space-y-2 text-sm">
              {["Your photo, with optional background removal", "Name, rating, position, shirt number and six stats", "Club crest, club colours and a country flag, county or province badge", "Football, Gaelic football, hurling/camogie and rugby templates", "Printed in vivid colour, cut to the shield shape"].map((t) => (
                <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-lime-600" />{t}</li>
              ))}
            </ul>
            <Link to="/design" className="btn-blaze mt-7 inline-block px-6 py-3 text-sm" data-testid="shields-design-button">Design your shield</Link>
          </div>
          <img src="/img/personalised-sports-shield-bedroom-wall.jpg" width="1264" height="848" alt="Personalised green and white Gaelic football player shield hanging on a child's bedroom wall"
            className="w-full border-2 border-slate-900 shadow-[8px_8px_0_0_#0f172a]" data-testid="shields-hero-image" />
        </section>

        <section className="mt-16" aria-labelledby="sizes-h">
          <h2 id="sizes-h" className="font-display text-3xl font-black uppercase">Sizes & prices</h2>
          <p className="mt-1 text-sm text-slate-600">All prices include VAT. Made to order. Delivery in Ireland: €8 for Small/Medium, €18 if your order includes Large or XL. Squad orders are delivered free to your coach or club.</p>
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SIZES.map((s) => (
              <div key={s.id} id={s.id} className="border-2 border-slate-200 bg-white p-5" data-testid={`shields-size-${s.id}`}>
                <h3 className="font-display text-xl font-extrabold uppercase">{s.name}</h3>
                <p className="font-mono-cc text-xs text-slate-500">Shield size {shieldMm(s).w} × {s.h} mm{s.recommended ? " · Recommended" : ""}</p>
                <p className="font-cond mt-3 text-3xl font-bold" data-testid={`shields-price-${s.id}`}>{eur(s.price)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" aria-labelledby="chart-h" id="size-chart">
          <h2 id="chart-h" className="font-display text-3xl font-black uppercase">Size chart</h2>
          <p className="mt-1 text-sm text-slate-600">Every size drawn to scale next to a 1.2 m (4 ft) child, a football and an A4 sheet.</p>
          <div className="mt-6"><SizeChart /></div>
        </section>

        <section className="mt-16" aria-labelledby="wall-h" id="on-the-wall">
          <h2 id="wall-h" className="font-display text-3xl font-black uppercase">See it on a wall</h2>
          <p className="mt-1 text-sm text-slate-600">Tap a size to see how big the shield looks above a 90 cm single bed. In the designer you'll see your own shield here.</p>
          <div className="mt-6 max-w-4xl"><WallPreview design={SAMPLES[1]} initialSize="large" /></div>
        </section>

        <section className="mt-16 grid gap-10 lg:grid-cols-2" aria-labelledby="styles-h">
          <div>
            <h2 id="styles-h" className="font-display text-3xl font-black uppercase">Sports & statistics</h2>
            <dl className="mt-5 space-y-3">
              {Object.values(TEMPLATES).map((t) => (
                <div key={t.name} className="border-2 border-slate-200 bg-white p-4">
                  <dt className="font-display text-lg font-extrabold uppercase">{t.name}</dt>
                  <dd className="text-sm text-slate-600">{t.stats.map((s) => `${s.label} (${s.key})`).join(", ")}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="grid grid-cols-3 items-end gap-3" aria-label="Example shields">
            {SAMPLES.map((d) => (
              <figure key={d.sport} className="m-0">
                <ShieldPreview design={d} testId={`shields-sample-${d.sport}`} title={`Example ${TEMPLATES[d.sport].name} shield for ${d.player.name}`} />
                <figcaption className="mt-2 text-center text-xs font-semibold text-slate-500">{TEMPLATES[d.sport].name}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-16 border-2 border-slate-900 bg-slate-950 p-8 text-white" aria-labelledby="clubs-h">
          <h2 id="clubs-h" className="font-display text-3xl font-black uppercase">Ordering for a whole team?</h2>
          <p className="mt-2 max-w-xl text-slate-300">Clubs and schools can set one club look, share a parent link and fundraise on every shield.</p>
          <Link to="/clubs" className="btn-blaze mt-5 inline-block px-5 py-3 text-sm" data-testid="shields-clubs-link">Club fundraising</Link>
        </section>
      </main>
      <ReviewStrip />
      <PublicFooter />
    </div>
  );
}
